import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import Api, { withAcl } from 'hooks/Api';
import { useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import type { DraggableLocation } from 'react-beautiful-dnd';

import c from '../utils/constants';

type Payload = {
  pipelineId: string;
  query: Record<string, any>;
  source: DraggableLocation;
  destination: DraggableLocation;
  draggableId: string;
};

export default function useUpdateDealSequence() {
  const queryClient = useQueryClient();
  const { state } = useProvider();
  const { accountId, accountUserId } = useAdminParams();
  const args = state;

  return useMutation({
    mutationFn: async (payload: Payload) => {
      const items:
        | Record<string, Array<{ _id: string; sequence: number }>>
        | undefined = queryClient.getQueryData([
        c.PIPELINES,
        payload.pipelineId,
        c.DEALS,
        c.BOARD,
        payload.query,
      ]);

      if (!items) return null;

      const newItems: Record<
        string,
        Array<{ _id: string; sequence: number }>
      > = { ...items };

      if (!newItems?.[payload.destination.droppableId]) {
        newItems[payload.destination.droppableId] = [];
      }

      const [removed] = newItems[payload.source.droppableId].splice(
        payload.source.index,
        1,
      );
      newItems?.[payload.destination.droppableId].splice(
        payload.destination.index,
        0,
        removed,
      );

      queryClient.setQueryData(
        [c.PIPELINES, payload.pipelineId, c.DEALS, c.BOARD, payload.query],
        newItems,
      );

      const api = new Api(args);

      const data = await api.patch(
        await withAcl({
          account: {
            url: `/api/v1/sales/deals/${payload.draggableId}/reorder`,
            payload: {
              stageId: payload.destination.droppableId,
              prevItems:
                items[payload.source.droppableId].map((item, index) => ({
                  id: item._id,
                  sequence: index + 1,
                })) ?? [],
              items: newItems[payload.destination.droppableId].map(
                ({ _id }, index) => ({
                  id: _id,
                  sequence: index + 1,
                }),
              ),
            },
          },
          admin: {
            // eslint-disable-next-line max-len
            url: `/api/v2/accounts/${accountId}/users/${accountUserId}/sales/deals/${payload.draggableId}/reorder`,
            payload: {
              stageId: payload.destination.droppableId,
              prevItems:
                items[payload.source.droppableId].map((item, index) => ({
                  id: item._id,
                  sequence: index + 1,
                })) ?? [],
              items: newItems[payload.destination.droppableId].map(
                ({ _id }, index) => ({
                  id: _id,
                  sequence: index + 1,
                }),
              ),
            },
          },
        }),
      );

      return data;
    },
    onSuccess: (_, payload) => {
      const queries = queryClient.getQueriesData([
        c.PIPELINES,
        payload.pipelineId,
        c.DEALS,
        c.BOARD,
      ]);

      queries?.forEach((query) => {
        if (!isEqual(query?.[0]?.[4], payload.query)) {
          queryClient.invalidateQueries({ queryKey: query?.[0], exact: true });
        }
      });

      queryClient.invalidateQueries({
        queryKey: [c.PIPELINES, payload.query.pipelineId, c.DEALS, c.LIST],
      });

      queryClient.invalidateQueries({
        queryKey: [c.PIPELINES, c.DEALS],
      });
    },
  });
}
